import { Daycare } from '@/types/Daycare'
import { GenerateNewOccasionalSlotsProps, NewOccasionalSlotsBuilder } from '../../models/newOccasionalSlotsBuilder'
import { OccasionalSlotsValidationErrors, OccasionalSlotsValidationProps } from '../../models/occasionalSlotsValidation'
import { UpdateOccasionalSlotsBuilder } from '../../models/updateOccasionalSlotsBuilder'
import { OccasionalSlotFormProps } from '../types'
import { Submission, useOccasionalSlotSubmission } from './useOccasionalSlotSubmission'

interface UseOccasionalSlotFormSubmissionProps {
    formProps: OccasionalSlotFormProps
    daycare: Daycare
    validation: OccasionalSlotsValidationProps
    slotDetails: Omit<GenerateNewOccasionalSlotsProps, 'start' | 'end' | 'sectionId'>
    onValidation: (errors: OccasionalSlotsValidationErrors) => void
    onScheduleRequest: () => void
}

export function useOccasionalSlotFormSubmission({
    formProps,
    daycare,
    validation,
    slotDetails,
    onValidation,
    onScheduleRequest
}: UseOccasionalSlotFormSubmissionProps) {
    const { submit } = useOccasionalSlotSubmission({ onSuccess: formProps.handleClose })

    async function handleConfirm(isScheduled: boolean = false, scheduleConfirmed: boolean = false): Promise<void> {
        const { type, refetchData } = formProps
        const { dates, start, end, sectionId, confirmedChildIds } = validation
        const count: number = type === 'edit' ? 1 : validation.count
        if (type !== 'add') isScheduled = Boolean(slotDetails.scheduled) && confirmedChildIds.length === 0

        const builder: NewOccasionalSlotsBuilder | UpdateOccasionalSlotsBuilder =
            type === 'add'
                ? new NewOccasionalSlotsBuilder(daycare)
                : new UpdateOccasionalSlotsBuilder({
                      daycare,
                      slotsToUpdate: type === 'edit' ? [formProps.occasionalSlot] : formProps.occasionalSlots
                  })
        const errors: OccasionalSlotsValidationErrors = builder.getErrors({ ...validation, count })
        onValidation(errors)
        if (Object.values(errors).some(Boolean)) return

        if (type === 'add' && isScheduled && !scheduleConfirmed) {
            onScheduleRequest()
            return
        }

        const slotProps: GenerateNewOccasionalSlotsProps = { ...slotDetails, start, end, sectionId: sectionId! }
        try {
            let submission: Submission
            if (builder instanceof NewOccasionalSlotsBuilder) {
                submission = {
                    type: 'add',
                    slots: builder.generate({ slotProps, count, dates, confirmedChildIds, isScheduled }),
                    scheduled: isScheduled
                }
            } else {
                if (dates.mode !== 'single') throw new Error('A single date is required when editing slots')
                submission = {
                    type: 'edit',
                    slots: builder.generate({
                        slotProps: { ...slotProps, date: dates.date },
                        count,
                        confirmedChildIds,
                        isScheduled
                    }),
                    scheduled: isScheduled
                }
            }
            await submit(submission)
        } finally {
            refetchData()
        }
    }

    return { handleConfirm }
}
